/**
 * Customer Intelligence tracker (client-only). Batches, dedupes, and
 * flushes events to the server. Also owns active-time detection,
 * scroll-milestone dedup, and pagehide summaries.
 *
 * Public surface:
 *   - track(name, props)         fire-and-forget event
 *   - pageView(path, props)      call on SPA navigation
 *   - markScroll(percent)        update session max scroll
 *   - markEngaged()              flag this session as engaged
 *
 * Backward-compatible: existing `import { track } from "@/lib/identity/tracker"`
 * continues to work; that module now forwards here.
 */
import { getAnonymousId } from "@/lib/identity/anonymousId";
import { SECTION_RANK } from "@/lib/repeat-interest";
import { ingestEventsFn } from "./ingest.functions";
// First-party operational telemetry is essential processing, not optional analytics consent.
const allowAnalytics = () => true;
import {
  getSession,
  touchSession,
  getPageViewId,
  getPagePath,
  resetPageView,
  newEventId,
} from "./session";

type QueuedEvent = {
  eventId: string;
  eventName: string;
  sessionId: string;
  pageViewId?: string | null;
  pagePath?: string | null;
  pageType?: string | null;
  productId?: string | null;
  properties?: Record<string, unknown>;
  occurredAt: string;
};

const FLUSH_INTERVAL_MS = 2000;
const MAX_BATCH = 20;
const MAX_PENDING = 1000;
const ACTIVE_IDLE_MS = 30_000; // no interaction for 30s => idle

const queue: QueuedEvent[] = [];
const seenEventIds = new Set<string>();
let flushTimer: number | null = null;
let started = false;
let flushing = false;
let pendingSummary = false;
let deliveryFailures = 0;

// ---- active time --------------------------------------------------------
let activeTimeMs = 0;
let totalTimeStart = Date.now();
let lastActivityAt = Date.now();
let lastTick = Date.now();
let visible = typeof document === "undefined" ? true : !document.hidden;
let maxScrollPct = 0;

// ---- per-page product engagement (repeat-interest signal) ---------------
// Accumulates per page view and flushes as a single `product_engagement`
// event on route change / pagehide. The server dedupes by
// (visitor, session, product), so refresh duplicates collapse there.
let pageActiveMs = 0;
let pageMaxScrollPct = 0;
let pageDeepestSection: string | null = null;
let pageHandle: string | null = null;

/** PDP calls this so the page's engagement summary is attributed to a product. */
export function setProductPageHandle(handle: string | null) {
  pageHandle = handle;
}

/** Called by section-view probes; keeps only the deepest known section. */
export function markSection(sectionId: string) {
  const rank = SECTION_RANK[sectionId] ?? -1;
  if (rank < 0) return; // unknown probes never emit
  if (!pageDeepestSection || rank > (SECTION_RANK[pageDeepestSection] ?? -1)) {
    pageDeepestSection = sectionId;
  }
}

function resetPageEngagement() {
  pageActiveMs = 0;
  pageMaxScrollPct = 0;
  pageDeepestSection = null;
  pageHandle = null;
}

/**
 * Emit the per-page engagement summary. Thresholds here are a cheap
 * anti-noise floor; the strict deep-visit rule (≥30s, ≥60% depth) is
 * enforced server-side in repeat-interest.ts.
 */
function emitEngagementSummary() {
  if (!pageHandle) return;
  if (pageActiveMs < 5_000 && pageMaxScrollPct < 30) return;
  track("product_engagement", {
    handle: pageHandle,
    activeTimeMs: Math.round(pageActiveMs),
    maxScrollPct: pageMaxScrollPct,
    deepestSection: pageDeepestSection,
    sectionRank: pageDeepestSection ? (SECTION_RANK[pageDeepestSection] ?? 0) : 0,
  });
}

function tickActive() {
  const now = Date.now();
  const dt = now - lastTick;
  lastTick = now;
  if (visible && now - lastActivityAt < ACTIVE_IDLE_MS) {
    activeTimeMs += dt;
    pageActiveMs += dt;
  }
}

function detectDevice(): "mobile" | "tablet" | "desktop" {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobile|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

function detectBrowser(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  if (/Firefox\//.test(ua)) return "firefox";
  return "other";
}

function sessionPayload() {
  const s = getSession();
  return {
    sessionId: s.sessionId,
    startedAt: new Date(s.startedAt).toISOString(),
    landingPage: s.landingPage,
    referrer: s.referrer,
    utm: {
      utm_source: s.utm.utm_source ?? null,
      utm_medium: s.utm.utm_medium ?? null,
      utm_campaign: s.utm.utm_campaign ?? null,
      utm_content: s.utm.utm_content ?? null,
      utm_term: s.utm.utm_term ?? null,
    },
    deviceType: detectDevice(),
    browserFamily: detectBrowser(),
  };
}

async function flush(includeSummary = false) {
  if (typeof window === "undefined") return;
  // A withdrawal must stop queued measurement too, not just new events.
  if (!allowAnalytics()) {
    queue.length = 0;
    pendingSummary = false;
    return;
  }
  pendingSummary ||= includeSummary;
  if (flushing || (queue.length === 0 && !pendingSummary)) return;
  const anonymousId = getAnonymousId();
  if (!anonymousId) return;
  includeSummary = pendingSummary;
  pendingSummary = false;
  flushing = true;
  const batch = queue.splice(0, MAX_BATCH);
  if (batch.length === 0 && !includeSummary) return;
  // Ensure the server always has at least a heartbeat event so it can
  // upsert session/aggregates on pagehide.
  const events = batch.length
    ? batch
    : [buildEvent("session_heartbeat", {})];
  const totalTimeMs = Date.now() - totalTimeStart;
  const payload = {
    anonymousId,
    session: sessionPayload(),
    events,
    ...(includeSummary
      ? {
          summary: {
            maxScrollPercent: maxScrollPct,
            activeTimeMs,
            totalTimeMs,
            exitPage: typeof window !== "undefined" ? window.location.pathname : null,
            engaged: activeTimeMs > 15_000 || maxScrollPct >= 50,
          },
        }
      : {}),
  };
  try {
    const result = await ingestEventsFn({ data: payload });
    if (result && !result.ok && (result.reason as string) === "consent_required") {
      queue.length = 0;
      pendingSummary = false;
      return;
    }
    if (!result?.ok) throw new Error("analytics_delivery_failed");
    deliveryFailures = 0;
  } catch (err) {
    // Includes resolved ok:false, not only network exceptions. Original event
    // IDs survive retries, so the existing customer_events unique key dedupes.
    if (allowAnalytics()) {
      queue.unshift(...batch);
      pendingSummary ||= includeSummary;
    } else {
      queue.length = 0;
      pendingSummary = false;
    }
    deliveryFailures++;
    if (deliveryFailures === 1) console.warn("[ci:tracker] delivery failed; retry queued");
  } finally {
    flushing = false;
    if (queue.length || pendingSummary) {
      // Override an earlier normal timer so fresh events cannot bypass backoff.
      if (flushTimer != null) { window.clearTimeout(flushTimer); flushTimer = null; }
      scheduleFlush(deliveryFailures ? Math.min(30_000, 2000 * 2 ** Math.min(deliveryFailures - 1, 4)) : FLUSH_INTERVAL_MS);
    }
  }
}

function scheduleFlush(delay = FLUSH_INTERVAL_MS) {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flush();
  }, delay);
}

function buildEvent(name: string, props: Record<string, unknown>, eventId?: string): QueuedEvent {
  const s = getSession();
  return {
    eventId: eventId ?? newEventId(),
    eventName: name,
    sessionId: s.sessionId,
    pageViewId: getPageViewId(),
    pagePath: getPagePath(),
    pageType: typeof props.pageType === "string" ? (props.pageType as string) : null,
    productId: typeof props.productId === "string" ? (props.productId as string) : null,
    properties: props,
    occurredAt: new Date().toISOString(),
  };
}

export function track(name: string, props: Record<string, unknown> = {}, eventId?: string) {
  if (typeof window === "undefined") return;
  // First-party but visitor-identified: optional Analytics, not essential.
  if (!allowAnalytics()) return;
  ensureStarted();
  touchSession();
  if (queue.length >= MAX_PENDING) {
    console.warn("[ci:tracker] queue full; event not queued");
    return;
  }
  const evt = buildEvent(name, props, eventId);
  if (seenEventIds.has(evt.eventId)) return;
  seenEventIds.add(evt.eventId);
  queue.push(evt);
  if (queue.length >= MAX_BATCH && deliveryFailures === 0) {
    void flush();
  } else {
    scheduleFlush();
  }
}

export function pageView(path: string, props: Record<string, unknown> = {}) {
  emitEngagementSummary(); // flush the page we're leaving, then reset
  resetPageEngagement();
  resetPageView(path);
  maxScrollPct = 0;
  track("page_view", { path, referrer: typeof document !== "undefined" ? document.referrer : "", ...props });
}

export function markScroll(percent: number) {
  if (percent > maxScrollPct) maxScrollPct = Math.min(100, Math.round(percent));
  const p = Math.min(100, Math.round(percent));
  if (p > pageMaxScrollPct) pageMaxScrollPct = p;
}

export function markEngaged() {
  activeTimeMs = Math.max(activeTimeMs, 15_001);
}

function ensureStarted() {
  if (started || typeof window === "undefined") return;
  started = true;
  totalTimeStart = Date.now();
  lastTick = Date.now();
  lastActivityAt = Date.now();

  const bump = () => {
    lastActivityAt = Date.now();
  };
  const events: Array<keyof WindowEventMap> = [
    "mousemove",
    "keydown",
    "click",
    "touchstart",
    "scroll",
    "wheel",
    "focus",
  ];
  for (const e of events) window.addEventListener(e, bump, { passive: true });

  document.addEventListener("visibilitychange", () => {
    tickActive();
    visible = !document.hidden;
    if (document.hidden) void flush(true);
  });

  window.setInterval(tickActive, 1000);

  // pagehide is the reliable "leaving" signal on modern browsers.
  const finalize = () => {
    tickActive();
    emitEngagementSummary();
    // If we can, use sendBeacon path; otherwise, keepalive fetch via our fn.
    void flush(true);
  };
  window.addEventListener("pagehide", finalize);
  window.addEventListener("beforeunload", finalize);
}
